import { useEffect, useState } from "react";
import api from "../lib/api";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between text-[#3A3A30]" data-testid={testid}>
    <span className="text-[#6B6B5E]">{label}</span>
    <span>{value}</span>
  </div>
);

// Renders a complete Define Salons invoice/receipt. Fetches the enriched
// public invoice payload by id (endpoint accepts public_token OR raw ObjectId).
export default function InvoiceView({ id }) {
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setInv(null); setError(false);
    api.get(`/public/invoice/${id}`).then((r) => setInv(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) return <div className="p-10 text-center text-muted-foreground" data-testid="invoice-view-error">Invoice not found.</div>;
  if (!inv) return <div className="p-10 text-center text-muted-foreground" data-testid="invoice-view-loading">Loading invoice…</div>;

  const dateTime = [inv.bill_date, inv.bill_time].filter(Boolean).join("  ");

  return (
    <div className="bg-white text-[#15241C] w-full" data-testid="invoice-view">
      {/* Header */}
      <div className="bg-[#15241C] text-white px-6 sm:px-8 py-6 text-center">
        <h1 className="font-serif text-2xl sm:text-3xl tracking-wide text-[#C5A059]" data-testid="invoice-view-salon">DEFINE SALONS</h1>
        {inv.branch_name && <p className="text-sm mt-2 text-white/90">{inv.branch_name}</p>}
        {inv.branch_address && <p className="text-xs mt-1 text-white/70">{inv.branch_address}</p>}
        {inv.branch_phone && <p className="text-xs mt-1 text-white/70">Phone: {inv.branch_phone}</p>}
        <p className="text-xs mt-1 font-medium text-[#C5A059]" data-testid="invoice-view-gst">GST No.: {inv.gst_no}</p>
      </div>

      {/* Meta */}
      <div className="px-6 sm:px-8 py-5 grid grid-cols-2 gap-3 text-sm border-b border-[#E5DFD2]">
        <div>
          <p className="text-[#6B6B5E]">Invoice No.</p>
          <p className="font-mono font-semibold" data-testid="invoice-view-no">{inv.invoice_no}</p>
        </div>
        <div className="text-right">
          <p className="text-[#6B6B5E]">Date</p>
          <p className="font-medium">{dateTime || "—"}</p>
        </div>
        <div>
          <p className="text-[#6B6B5E]">Customer</p>
          <p className="font-medium">{inv.customer_name || "—"}</p>
        </div>
        <div className="text-right">
          <p className="text-[#6B6B5E]">Mobile</p>
          <p className="font-mono">{inv.customer_mobile_masked || "—"}</p>
        </div>
      </div>

      {/* Items */}
      <div className="px-6 sm:px-8 py-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="text-left text-[#6B6B5E] border-b border-[#E5DFD2]">
              <th className="pb-2 font-medium">Service / Product</th>
              <th className="pb-2 font-medium">Staff</th>
              <th className="pb-2 font-medium text-center">Qty</th>
              <th className="pb-2 font-medium text-right">Price</th>
              <th className="pb-2 font-medium text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items?.map((it, i) => (
              <tr key={i} className="border-b border-[#F0ECE2]" data-testid={`invoice-view-item-${i}`}>
                <td className="py-2">{it.name}</td>
                <td className="py-2 text-[#6B6B5E]">{it.staff_name || "—"}</td>
                <td className="py-2 text-center">{it.qty}</td>
                <td className="py-2 text-right">{rupee(it.price)}</td>
                <td className="py-2 text-right font-medium">{rupee(it.net_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="px-6 sm:px-8 pb-5">
        <div className="ml-auto max-w-xs text-sm space-y-1.5">
          <Row label="Subtotal" value={rupee(inv.subtotal)} />
          {inv.item_discount_total > 0 && <Row label="Service Discounts" value={`- ${rupee(inv.item_discount_total)}`} />}
          {inv.discount > 0 && <Row label="Overall Discount" value={`- ${rupee(inv.discount)}`} />}
          {inv.tax > 0 && <Row label="Taxable Value" value={rupee(inv.taxable_value)} />}
          {inv.tax > 0 && <Row label={`GST (${Math.round(inv.tax_percent)}% incl.)`} value={rupee(inv.tax)} />}
          <div className="flex justify-between pt-2 mt-1 border-t border-[#C5A059] text-lg font-serif text-[#15241C]" data-testid="invoice-view-total">
            <span>Total</span>
            <span>{rupee(inv.grand_total)}</span>
          </div>
        </div>
      </div>

      {/* Payments */}
      {(inv.payments?.length > 0 || inv.paid_amount > 0) && (
        <div className="px-6 sm:px-8 pb-6 text-sm">
          <div className="bg-[#F5F0E6] rounded-md px-4 py-3 max-w-xs ml-auto space-y-1">
            <div className="flex justify-between pb-1 mb-1 border-b border-[#DDD5C4]" data-testid="invoice-view-mode">
              <span className="text-[#7A6E52]">Mode of Payment</span>
              <span className="font-medium">{(inv.payments?.length || 0) > 1 ? "Split Payment" : (inv.payments?.[0]?.method || "—")}</span>
            </div>
            {inv.payments?.map((p, i) => (
              <Row key={i} label={p.method} value={rupee(p.amount)} testid={`invoice-view-payment-${i}`} />
            ))}
            <div className="flex justify-between pt-1 border-t border-[#DDD5C4] font-medium">
              <span>Total Paid</span>
              <span data-testid="invoice-view-paid">{rupee(inv.paid_amount)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[#15241C] text-center py-4 text-xs text-white/60">
        Thank you for choosing DEFINE SALONS.
      </div>
    </div>
  );
}
